import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../firebase';
import { ADMINS_REF } from './adminConstants';

export const AdminToastContext = createContext(null);

export function useAdminToast() {
  const ctx = useContext(AdminToastContext);
  return ctx;
}

const ADMIN_PAGE_TITLES = {
  '/admin/watchlists': 'Watchlists',
  '/admin/configurations': 'Configurations',
  '/admin/users': 'Users',
};

function AdminLayout({ user }) {
  const location = useLocation();
  const [adminChecked, setAdminChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const toastTimeoutsRef = useRef([]);

  const showToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message }]);
    const t = window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
    toastTimeoutsRef.current.push(t);
  };

  useEffect(() => {
    return () => {
      toastTimeoutsRef.current.forEach(clearTimeout);
      toastTimeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setAdminChecked(true);
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const adminSnap = await get(ref(db, `${ADMINS_REF}/${user.uid}`));
        if (!cancelled) {
          setAdminChecked(true);
          setIsAdmin(adminSnap.exists());
        }
      } catch (err) {
        if (!cancelled) {
          setAdminChecked(true);
          setIsAdmin(false);
          setError(err.message || 'Failed to verify admin access');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!adminChecked) {
    return (
      <div className="app-loading-screen" role="status" aria-live="polite">
        <div className="spinner" aria-label="Loading" />
        <p>Loading…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/analysis" replace />;
  }

  return (
    <div className="admin-page-wrap">
      <div className="admin-page">
        <header className="admin-header">
          <h1 className="admin-title">{ADMIN_PAGE_TITLES[location.pathname] ?? 'Admin'}</h1>
        </header>
        {error && (
          <div className="admin-error" role="alert">
            {error}
          </div>
        )}
        <AdminToastContext.Provider value={{ showToast }}>
          <Outlet />
        </AdminToastContext.Provider>
        <div className="toast-container" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className="toast">
              {toast.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
